// ---------------------------------------------------------------------------
// Viz DSL Renderer — main entry point and block dispatcher.
//
// Block renderers have been extracted to ui/src/renderers/.
// The layout engine lives in ui/src/VizDslLayoutEngine.js.
// This file now serves as the slide-composer + registry-first dispatcher.
// ---------------------------------------------------------------------------

import Konva from "konva";
import CustomText from "./CustomText";
import CustomImage from "./CustomImage";
import { themeColor, defaultTheme } from "./renderers/plainObjectRenderer";
import { blockRegistry } from "./shared/block-registry/init.js";
// Wire Konva renderers to block definitions (must happen before any renderBlock call)
import "./blockRegistryInit.js";

// Re-export renderers for backward compatibility (DocDslRenderer, tests)
export {
  renderTextBlock,
  renderChart,
  renderTileRow,
  renderBulletList,
  renderConditional,
  renderMap,
  renderLegend,
  renderSectionHeader,
} from "./renderers/index.js";

export {
  renderFlow,
  isContentSized,
  measureNaturalHeight,
  childHasData,
  selectVisibleChildren,
  canStretchInPhase4,
} from "./VizDslLayoutEngine.js";

// Re-export cross-renderer helpers (imported by extracted renderers)
export {
  addDebugOverlay,
  tagEvidence,
  normalizeTile,
  resolveTileScaleGroups,
} from "./renderers/helpers.js";

// ---------------------------------------------------------------------------
// Dummy DSL and crystalized data for testing
// ---------------------------------------------------------------------------

export const DUMMY_DSL = {
  version: 1,
  kind: "slide",
  title: "Financial posture — Diageo",
  intent: "Snapshot of financial health for Diageo.",
  pins: [
    { name: "company", type: "entity", label: "Company", required: true },
    { name: "financials", type: "financial", label: "Financial data", required: false }
  ],
  flow: [
    {
      type: "flow", direction: "horizontal",
      children: [
        {
          type: "text_block", id: "sidebar", proportion: 0.4, label: "Financial posture",
          slots: [
            { role: "llm_gen", key: "takeaway", max_words: 8, style: "italic" },
            { role: "llm_gen", key: "evidence", max_words: 50, min_words: 35 }
          ]
        },
        {
          type: "flow", direction: "vertical", proportion: 0.6,
          children: [
            {
              type: "chart", id: "revenue_chart", kind: "bar", proportion: 1.2,
              data_binding: { pin: "financials", limit: 4, label_field: "fiscal_year", label_format: "FY{value}" },
              series: [
                { key: "revenue", label: "Revenue", color: "#1F3A2E", label_position: "above_bar", label_format: "currency" },
                { key: "net_profit", label: "Net profit", color: { op: "cond", branches: [
                  { when: { field: "net_profit_yoy", op: "gt", value: 0 }, then: "#7FA690" },
                  { when: { field: "net_profit_yoy", op: "lt", value: -0.15 }, then: "#BA7517" }
                ], default: "#7FA690" }, label_position: "above_bar", label_format: "currency" }
              ],
              y_axis: { gridlines: { dashed: true, steps: [0.25, 0.50, 0.75, 1.0] }}, null_fallback: null
            },
            {
              type: "tile_row", id: "ratios", data_pin: "financials",
              null_fallback: { display: "N/A", color: "#999" },
              tiles: [
                { value: { formula: "net_profit / revenue * 100", format: "pct_1dp" }, label: { text: "Net margin" } },
                { value: { formula: "gross_profit / revenue * 100", format: "pct_1dp" }, label: { text: "Gross margin" } },
                { value: { formula: "net_debt / ebitda", format: "num_1dp", suffix: "x" }, label: { text: "Net debt/EBITDA" } },
                { value: { formula: "current_assets / current_liabilities", format: "num_1dp", suffix: "x", null_fallback: { display: "\u2014", color: "#999" } }, label: { text: "Current ratio" } },
                { value: { formula: "net_profit / equity * 100", format: "pct_1dp" }, label: { text: "Return on equity" } },
                { value: { extract: "free_cash_flow", format: "currency", null_fallback: { display: "Not disclosed", color: "#999" } }, label: { text: "Free cash flow" } }
              ]
            }
          ]
        }
      ]
    },
    {
      type: "conditional", id: "stock_section",
      branches: [
        { when: { field: "is_listed", op: "eq", value: true }, then: {
          type: "flow", direction: "horizontal",
          children: [
            { type: "chart", id: "stock_price", kind: "line", proportion: 0.7, data_binding: { pin: "stock_prices", limit_years: 3, label_field: "date" }, series: [{ key: "adj_close", label: "Adjusted close", color: "#1F3A2E" }], last_point: { dot_color: { op: "cond", branches: [{ when: { field: "price_change_3yr", op: "lt", value: 0 }, then: "#BA7517" }], default: "#2D8659" } } },
            { type: "text_block", id: "stock_stats", proportion: 0.3, slots: [
              { role: "llm_gen", key: "price_change_3yr", format: "pct_1dp", style: "bold", label: "3-year change" },
              { role: "llm_gen", key: "market_cap", format: "currency", label: "Market cap" },
              { role: "llm_gen", key: "pe_ratio", format: "num_1dp", label: "P/E (ttm)" }
            ]}
          ]
        }},
        { when: { field: "is_listed", op: "eq", value: false }, then: {
          type: "text_block", id: "private_notice",
          slots: [
            { role: "literal", value: "Stock data not applicable", style: "italic_serif_center" },
            { role: "literal", value: "Private company. Capital structure summarized via recent recap and credit actions." },
            { role: "llm_gen", key: "capital_summary", max_words: 60 }
          ]
        }}
      ]
    }
  ]
};

export const DUMMY_CRYSTALIZED = {
  sidebar: { takeaway: "Recapitalized; balance sheet materially de-risked.", evidence: "Revenue grew 12% YoY to $4.2B in FY25, though gross margins compressed 180bps. Net debt reduced from 3.2x to 1.8x EBITDA following a $500M equity raise in Q3 2024. Cash reserves stand at $1.1B." },
  revenue_chart: { x_labels: ["FY22","FY23","FY24","FY25"], series: { revenue: [3100,3400,3750,4200], net_profit: [150,180,290,380] }, net_profit_yoy: [0,0.20,0.61,0.31] },
  ratios: { net_profit: 380, revenue: 4200, gross_profit: 1776, net_debt: 684, ebitda: 380, current_assets: null, current_liabilities: null, equity: 2100, free_cash_flow: 215 },
  stock_section: { is_listed: false }
};

// ---------------------------------------------------------------------------
// Block dispatcher — registry-first, no fallback needed (all blocks registered)
// ---------------------------------------------------------------------------

// Walk leaf shapes and set _blockId so canvas clicks can identify the block.
// Inner blocks tag first; the "don't overwrite" guard ensures the most specific
// block wins (e.g. a chart inside a flow → chart.id, not flow.id).
function _tagBlockShapes(node, blockId) {
  if (!(node instanceof Konva.Group)) {
    if (!node.getAttr("_blockId")) node.setAttr("_blockId", blockId);
    return;
  }
  for (const child of node.getChildren()) _tagBlockShapes(child, blockId);
}

export function renderBlock(block, crystalizedData, options) {
  if (!block) return new Konva.Group();
  const def = blockRegistry.getIfRegistered(block.type);
  const group = (def && def.renderer && def.renderer.render)
    ? def.renderer.render(block, crystalizedData, options)
    : new Konva.Group();
  if (block.id && !options._measureOnly) _tagBlockShapes(group, block.id);
  return group;
}

// ---------------------------------------------------------------------------
// Tree flattener
// ---------------------------------------------------------------------------

function isContainerOnly(group) {
  if (!(group instanceof Konva.Group)) return false;
  const a = group.attrs;
  if (a.__async) return false;
  if (a.fill && a.fill !== "transparent") return false;
  if (a.stroke || a.strokeWidth) return false;
  if (a.cornerRadius) return false;
  if (a.opacity !== undefined && a.opacity < 1) return false;
  if (a.shadowEnabled) return false;
  if (a.clipX !== undefined || a.clipY !== undefined || a.clipWidth !== undefined || a.clipHeight !== undefined) return false;
  if (group.listening && !group.listening()) return false;
  if (a.background) return false;
  return true;
}

function flattenGroupTree(root) {
  let changed = true;
  while (changed) changed = _flattenPass(root);
}

function _flattenPass(group) {
  let changed = false;
  const children = group.getChildren();
  const toFlatten = children.filter(isContainerOnly);
  for (const container of toFlatten) {
    changed = true;
    const dx = container.x(), dy = container.y();
    for (const gc of container.getChildren().slice()) {
      gc.moveTo(group);
      gc.x(gc.x() + dx);
      gc.y(gc.y() + dy);
    }
    container.destroy();
  }
  for (const child of group.getChildren()) {
    if (child instanceof Konva.Group && _flattenPass(child)) changed = true;
  }
  return changed;
}

// ---------------------------------------------------------------------------
// Main entry point
// ---------------------------------------------------------------------------

function _neutralizePronouns(str) {
  return str
    .replace(/\bshe\b/g, 'they').replace(/\bShe\b/g, 'They')
    .replace(/\bhe\b/g, 'they').replace(/\bHe\b/g, 'They')
    .replace(/\bhis\b/g, 'their').replace(/\bHis\b/g, 'Their')
    .replace(/\bhim\b/g, 'them').replace(/\bHim\b/g, 'Them')
    .replace(/\bherself\b/g, 'themselves').replace(/\bHerself\b/g, 'Themselves')
    .replace(/\bhimself\b/g, 'themselves').replace(/\bHimself\b/g, 'Themselves')
    .replace(/\bher\b/g, 'their').replace(/\bHer\b/g, 'Their');
}
function _applyGenderNeutral(data) {
  if (typeof data === 'string') return _neutralizePronouns(data);
  if (Array.isArray(data)) return data.map(_applyGenderNeutral);
  if (data && typeof data === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(data)) out[k] = _applyGenderNeutral(v);
    return out;
  }
  return data;
}

export function renderVizDslSlide(dsl, crystalizedData, options = {}) {
  const { width = 1920, imageCallback, theme, debug = false, sourcePins } = options;
  const height = options.height || Math.round(width * 9 / 16);
  const fontScale = options.fontScale || (width / 1280);
  const resolvedTheme = theme || dsl.theme || defaultTheme;
  const group = new Konva.Group({ width, height });
  let bodyTop = 0;

  // Uncrystallized / preview mode: when no crystalized data is provided,
  // render the DSL structure as a preview with placeholder content. A
  // `__real: true` marker counts as "provided" for this check even though
  // it carries no block data — for a DSL that was genuinely crystallized
  // but has NO extract/llm_gen blocks at all (e.g. a fully literal
  // chapter-divider slide), crystallization correctly produces an empty
  // object, which isn't the same thing as "never crystallized," and
  // shouldn't fall back to skeleton placeholders.
  const hasRealMarker = Boolean(crystalizedData && typeof crystalizedData === "object" && crystalizedData.__real);
  if (hasRealMarker) {
    const { __real, ...rest } = crystalizedData;
    crystalizedData = rest;
  }
  const isPreview = !hasRealMarker && (!crystalizedData || (typeof crystalizedData === "object" && Object.keys(crystalizedData).length === 0));
  if (isPreview) {
    crystalizedData = {};
    options._preview = true;
  }

  if (!isPreview && dsl.gender_neutral) {
    crystalizedData = _applyGenderNeutral(crystalizedData);
  }

  group.add(new Konva.Rect({ width, height, fill: themeColor(resolvedTheme, "surface", "#fafafa") }));

  // Full-bleed background image (e.g. a cover photo) — declared at
  // dsl.chrome.background_image: { src, overlay_color?, overlay_opacity? }.
  // Renders immediately after the base surface fill (so it's still behind
  // the pixel motif / header / body content, all of which render normally
  // on top afterward). overlay_color/overlay_opacity draw a flat color
  // wash over the photo for text legibility — not a replica of any
  // specific gradient, just enough contrast for white text on top.
  const bgImage = dsl.chrome?.background_image;
  if (bgImage?.src) {
    if (!isPreview) {
      group.add(new CustomImage({
        url: bgImage.src,
        x: 0, y: 0, width, height,
        placeholder: false,
        // CustomImage defaults to maxScale:10 (supersampling for interactive
        // zoom in a live canvas). A full-bleed background rendered once at
        // its final display size doesn't need that — at the default, a
        // 1920-wide image rasters an ~19200px-wide buffer internally, which
        // is harmless for PNG (flattened to the stage's own pixelRatio) but
        // gets embedded VERBATIM in PPTX export (a 90MB single-image PPTX).
        maxScale: 2,
        refreshCallback: imageCallback,
      }));
    }
    if (bgImage.overlay_color) {
      group.add(new Konva.Rect({
        width, height,
        fill: themeColor(resolvedTheme, bgImage.overlay_color, bgImage.overlay_color),
        opacity: bgImage.overlay_opacity ?? 0.55,
      }));
    }
  }

  // Lowest z-order on the slide: added immediately after the plain surface
  // fill and before any chrome/header/body content, so it always renders
  // BEHIND everything else — a body content background that reaches into
  // this corner will now cover it, which is the intended trade-off.
  const addPixelMotif = () => {
    if (dsl.chrome?.motif !== "pixels") return;
    // Exact triangular dissolve grid, reverse-engineered by pixel-sampling
    // a real reference screenshot (RGB read directly off the image, not
    // eyeballed): 3 flat shades of one green, no gaps/opacity blending —
    // color depends only on diagonal distance (row+col) from the
    // top-right-most cell. Cell size is ~4.66% of slide width in the
    // reference (103px of a 2212px-wide render), reproduced here as a
    // fraction of `width` so it holds at any render resolution.
    const shades = ["#E2F7E3", "#F1FBF1", "#F8FDF8"];
    const size = Math.round(width * 0.0466 );
    // Flush with the true top-right corner of the slide canvas — no margin
    // indent from the header/content padding.
    const right = width;
    const top = 0;
    const cells = [];
    for (let row = 0; row <= 2; row++) {
      for (let col = 0; col <= 2 - row; col++) {
        cells.push({ row, col, color: shades[row + col] });
      }
    }
    cells.forEach((pixel) => group.add(new Konva.Rect({
      x: right - (pixel.col + 1) * size,
      y: top + pixel.row * size,
      width: size,
      height: size,
      fill: pixel.color,
    })));
  };
  addPixelMotif();

  // Section-divider grid overlay: a 14x14 grid of white squares at fixed
  // opacity levels, positioned in a square region right-of-center — pixel-
  // sampled from a real reference PPTX (image8.png), not approximated.
  // Declared via dsl.chrome.motif === "grid_overlay". Intended to sit over
  // a solid-color slide background (set via dsl.theme.palette.surface),
  // so — like the cover background image — this renders at chrome level,
  // immediately after the base surface fill, before any body content.
  const addGridOverlayMotif = () => {
    if (dsl.chrome?.motif !== "grid_overlay") return;
    // [row, col, opacity] for every non-empty cell of the reference's 14x14
    // grid (89 of 196 cells are empty). Region: x 65%-100% of width, y
    // 38.07%-100.3% of height (a square sized to 35% of width == 62.2% of
    // height, matching the reference's square EMU shape on a 16:9 slide).
    const cells = [
      [0,0,0.502],[0,1,0.4],[0,2,0.4],[0,3,0.4],[0,4,0.502],[0,5,0.302],[0,6,0.302],[0,10,0.302],[0,11,0.2],[0,12,0.2],[0,13,0.2],
      [1,0,0.502],[1,1,0.4],[1,2,0.4],[1,3,0.502],[1,4,0.4],[1,5,0.302],[1,12,0.098],[1,13,0.098],
      [2,0,0.502],[2,1,0.502],[2,2,0.4],[2,3,0.302],[2,7,0.2],[2,9,0.302],[2,10,0.2],[2,11,0.302],
      [3,0,0.502],[3,1,0.302],[3,2,0.4],[3,3,0.4],[3,4,0.4],[3,6,0.302],[3,7,0.302],[3,8,0.2],[3,10,0.098],[3,11,0.2],[3,12,0.098],
      [4,0,0.4],[4,1,0.4],[4,2,0.302],[4,3,0.302],[4,6,0.302],[4,7,0.302],[4,8,0.302],[4,11,0.098],[4,13,0.098],
      [5,0,0.4],[5,2,0.302],[5,6,0.302],[5,8,0.098],
      [6,1,0.4],[6,2,0.302],[6,6,0.2],[6,9,0.2],[6,10,0.2],[6,12,0.098],
      [7,1,0.302],[7,5,0.302],[7,7,0.302],[7,9,0.2],
      [8,0,0.4],[8,1,0.2],[8,2,0.2],[8,4,0.302],[8,6,0.2],[8,7,0.2],[8,10,0.098],
      [9,1,0.302],[9,3,0.2],[9,4,0.2],[9,5,0.2],[9,6,0.098],[9,7,0.098],[9,8,0.2],[9,9,0.098],[9,10,0.098],
      [10,0,0.2],[10,3,0.302],[10,4,0.2],
      [11,0,0.2],[11,1,0.2],[11,4,0.098],[11,5,0.2],[11,6,0.098],
      [12,2,0.2],[12,9,0.098],
      [13,2,0.2],[13,3,0.2],
    ];
    const regionSize = Math.round(width * 0.35);
    const regionX = Math.round(width * 0.65);
    const regionY = Math.round(height * 0.3807);
    const gridN = 14;
    const period = regionSize / gridN;
    const cellSize = Math.round(period * (80 / 96));
    cells.forEach(([row, col, opacity]) => group.add(new Konva.Rect({
      x: Math.round(regionX + (gridN - col) * period),
      y: Math.round(regionY + (gridN - row) * period),
      width: cellSize,
      height: cellSize,
      fill: "#FFFFFF",
      opacity,
    })));
  };
  addGridOverlayMotif();

  // Header
  if (dsl.chrome && dsl.chrome.header) {
    const hdr = dsl.chrome.header;
    const bandColor = hdr.band_color || "#1F3A2E";
    const stacked = hdr.layout === "stacked";
    const marginX = Math.round(20 * fontScale);
    // Resolve a chrome slot value from one of three sources, in order:
    //  - literal: the DSL itself carries the value
    //  - pin_text: read sourcePins[slot.pins[0]] live (re-renders track edits),
    //    falling back to the crystallized snapshot if no live pin is available
    //  - default: the crystallized snapshot (LLM-filled or otherwise)
    const readPinText = (slot) => {
      if (!sourcePins) return "";
      const pins = Array.isArray(slot?.pins) ? slot.pins : [];
      for (const pin of pins) {
        const v = sourcePins[pin];
        if (v == null) continue;
        if (typeof v === "string") return v.trim();
        // Unwrap arrays of strings (e.g. a title/breadcrumb passed as ["A"])
        // into a comma-joined string rather than raw JSON.
        if (Array.isArray(v)) {
          const joined = v
            .map(x => (typeof x === "string" ? x.trim() : (x == null ? "" : String(x))))
            .filter(Boolean).join(", ");
          if (joined) return joined;
          continue;
        }
        if (typeof v === "object") return JSON.stringify(v);
      }
      return "";
    };
    const resolveSlot = (slot) => {
      if (!slot) return "";
      if (slot.role === "literal") return slot.value || "";
      if (slot.role === "pin_text") {
        return readPinText(slot) || (slot.key ? (crystalizedData[slot.key] || "") : "");
      }
      return crystalizedData[slot.key] || crystalizedData.header_chips?.[slot.key]
        || crystalizedData.header_chips?.supplier_name || crystalizedData.supplier_name || "";
    };
    const supplierName = resolveSlot(hdr.supplier_name);
    // Small label at the top of the chrome band (the slot that previously held
    // a static string such as "PROBLEM ANALYSIS"). Prefer a slot-driven
    // secondary_header (with optional suffix), falling back to hdr.label.
    let smallLabel = "";
    const shSlot = dsl.chrome.secondary_header;
    if (shSlot) {
      smallLabel = resolveSlot(shSlot);
      if (shSlot.suffix && smallLabel) smallLabel += shSlot.suffix;
    } else if (hdr.label) {
      smallLabel = hdr.label;
    }
    const chips = hdr.chips || [];
    const resolved = chips.map(chip => {
      let chipValue = "";
      if (chip.role === "literal") chipValue = chip.value;
      else if (chip.role === "pin_text") chipValue = readPinText(chip) || (chip.key ? crystalizedData[chip.key] : "") || "";
      else if (chip.role === "extract" || chip.role === "derive") chipValue = crystalizedData[chip.key] || "";
      else if (chip.role === "llm_gen" || chip.role === "today") chipValue = crystalizedData.header_chips?.[chip.key] || "";
      return { label: chip.label, value: chipValue };
    }).filter(c => c.value);

    if (hdr.variant === "minimal") {
      const headerH = Math.round((hdr.height || 86) * fontScale);
      const headerX = Math.round((hdr.padding_x || 34) * fontScale);
      const eyebrow = hdr.eyebrow || smallLabel;
      const titleColor = hdr.text_color
        ? themeColor(resolvedTheme, hdr.text_color, hdr.text_color)
        : themeColor(resolvedTheme, "text", "#16181D");
      const eyebrowColor = hdr.eyebrow_color
        ? themeColor(resolvedTheme, hdr.eyebrow_color, hdr.eyebrow_color)
        : themeColor(resolvedTheme, "accent", "#08A6E8");
      bodyTop = headerH;
      // Skip the band fill entirely when it resolves to the same color as
      // the slide's own base surface — drawing a same-color rect over it
      // only hides whatever renders behind the header (e.g. the corner
      // pixel motif) for no visible benefit.
      const minimalBandFill = hdr.band_color
        ? themeColor(resolvedTheme, hdr.band_color, hdr.band_color)
        : themeColor(resolvedTheme, "surface", "#FFFFFF");
      const slideSurfaceFill = themeColor(resolvedTheme, "surface", "#FFFFFF");
      if (minimalBandFill !== slideSurfaceFill) {
        group.add(new Konva.Rect({ width, height: headerH, fill: minimalBandFill }));
      }
      if (eyebrow) {
        group.add(new CustomText({
          x: headerX,
          y: Math.round((hdr.eyebrow_y || 16) * fontScale),
          width: width - headerX * 2,
          text: String(eyebrow).toUpperCase(),
          fontSize: Math.round((hdr.eyebrow_size || 8) * fontScale),
          fontStyle: "bold",
          fontFamily: hdr.font_family || "Arial, sans-serif",
          letterSpacing: Math.round((hdr.eyebrow_spacing || 1.8) * fontScale),
          fill: eyebrowColor,
          refreshCallback: imageCallback,
        }));
      }
      group.add(new CustomText({
        x: headerX,
        y: Math.round((eyebrow ? (hdr.title_y || 34) : (hdr.title_y || 22)) * fontScale),
        width: width - headerX * 2,
        text: supplierName || dsl.title || "",
        fontSize: Math.round((hdr.title_size || 24) * fontScale),
        fontStyle: hdr.title_style || "bold",
        fontFamily: hdr.font_family || "Arial, sans-serif",
        fill: titleColor,
        lineHeight: 1.05,
        refreshCallback: imageCallback,
      }));
      if (hdr.rule_color) {
        group.add(new Konva.Rect({
          x: headerX,
          y: headerH - Math.max(1, Math.round(1 * fontScale)),
          width: width - headerX * 2,
          height: Math.max(1, Math.round(1 * fontScale)),
          fill: themeColor(resolvedTheme, hdr.rule_color, hdr.rule_color),
        }));
      }
    } else if (stacked) {
      const bandRect = new Konva.Rect({ width, height: 10, fill: bandColor });
      group.add(bandRect);
      const topPad = Math.round(10 * fontScale);
      let cy = topPad;
      if (smallLabel) {
        const ln = new CustomText({ x: marginX, y: cy, width: width - marginX * 2, text: smallLabel, fontSize: Math.round(12 * fontScale), fill: "rgba(255,255,255,0.7)", fontStyle: "bold", letterSpacing: Math.round(2 * fontScale), refreshCallback: imageCallback });
        group.add(ln); cy += ln.height() + Math.round(3 * fontScale);
      }
      if (supplierName) {
        const tn = new CustomText({ x: marginX, y: cy, width: width - marginX * 2, text: supplierName, fontSize: Math.round(30 * fontScale), fill: "#ffffff", fontStyle: "bold", fontFamily: "Georgia, serif", lineHeight: 1.05, wrap: true, refreshCallback: imageCallback });
        group.add(tn); cy += tn.height() + Math.round(5 * fontScale);
      }
      let cx = marginX; const chipFont = Math.round(12 * fontScale); let chipRowH = 0;
      for (const f of resolved) {
        if (f.label) {
          const ln = new CustomText({ x: cx, y: cy, text: `${f.label} `.toUpperCase(), fontSize: chipFont, fontStyle: "bold", fill: "rgba(255,255,255,0.55)", letterSpacing: Math.round(0.5 * fontScale), refreshCallback: imageCallback });
          group.add(ln); cx += ln.width() + Math.round(4 * fontScale); chipRowH = Math.max(chipRowH, ln.height());
        }
        const vn = new CustomText({ x: cx, y: cy, text: f.value, fontSize: chipFont, fill: "rgba(255,255,255,0.95)", refreshCallback: imageCallback });
        group.add(vn); cx += vn.width() + Math.round(24 * fontScale); chipRowH = Math.max(chipRowH, vn.height());
      }
      bandRect.height(cy + chipRowH + topPad);
      bodyTop = bandRect.height();
    } else {
      const headerH = Math.round(72 * fontScale);
      bodyTop = headerH;
      group.add(new Konva.Rect({ width, height: headerH, fill: bandColor }));
      if (smallLabel) group.add(new CustomText({ x: marginX, y: Math.round(6 * fontScale), width: width - Math.round(40 * fontScale), text: smallLabel, fontSize: Math.round(10 * fontScale), fill: "rgba(255,255,255,0.7)", fontStyle: "bold", letterSpacing: Math.round(2 * fontScale), refreshCallback: imageCallback }));
      const titleW = resolved.length > 0 ? Math.round(600 * fontScale) : width - marginX * 2;
      group.add(new CustomText({ x: marginX, y: Math.round(20 * fontScale), width: titleW, text: supplierName, fontSize: Math.round(34 * fontScale), fill: "#ffffff", fontStyle: "italic", fontFamily: "Georgia, serif", refreshCallback: imageCallback }));
      if (resolved.length > 0) {
        const rightPad = Math.round(20 * fontScale), fieldGap = Math.round(28 * fontScale), fieldW = Math.round(160 * fontScale);
        const totalW = resolved.length * fieldW + (resolved.length - 1) * fieldGap;
        const startX = width - rightPad - totalW;
        for (let i = 0; i < resolved.length; i++) {
          const f = resolved[i], fx = startX + i * (fieldW + fieldGap);
          if (f.label) group.add(new CustomText({ x: fx, y: Math.round(16 * fontScale), width: fieldW, text: f.label, fontSize: Math.round(10 * fontScale), fill: "rgba(255,255,255,0.55)", letterSpacing: Math.round(0.5 * fontScale), refreshCallback: imageCallback }));
          group.add(new CustomText({ x: fx, y: Math.round(34 * fontScale), width: fieldW, text: f.value, fontSize: Math.round(13 * fontScale), fontStyle: "bold", fill: "rgba(255,255,255,0.95)", refreshCallback: imageCallback }));
        }
      }
    }
  } else if (dsl.title) {
    const titleH = Math.round(56 * fontScale);
    bodyTop = titleH;
    group.add(new Konva.Rect({ width, height: titleH, fill: "#1F3A2E" }));
    group.add(new CustomText({ x: Math.round(20 * fontScale), y: 0, width: width - Math.round(40 * fontScale), height: titleH, text: dsl.title, fontSize: Math.round(20 * fontScale), fontStyle: "italic", fontFamily: "Georgia, serif", fill: "#ffffff", verticalAlign: "middle", refreshCallback: imageCallback }));
  }

  // Footer
  // Logo wordmark aspect ratio (width / height), measured from the source
  // asset (co-created-logo-{white,dark}.png, 2048x457) — fixed because both
  // hosted variants share the same crop.
  const LOGO_ASPECT = 4.4814;
  const COMPACT_FOOTER_VARIANTS = ["minimal", "badge", "chapter"];
  if (dsl.chrome && dsl.chrome.footer) {
    const ftr = dsl.chrome.footer, footerFontSize = Math.round(9 * fontScale);
    const isMinimalFooter = ftr.variant === "minimal";
    const isBadgeFooter = ftr.variant === "badge";
    const isChapterFooter = ftr.variant === "chapter";
    const isCompactFooter = COMPACT_FOOTER_VARIANTS.includes(ftr.variant);
    const footerH = Math.round((isCompactFooter ? 24 : 36) * fontScale), footerY = height - footerH;
    const textY = footerY + Math.round((footerH - footerFontSize) / 2);
    if (isBadgeFooter) {
      group.add(new Konva.Rect({ x: 0, y: footerY, width: Math.round(72 * fontScale), height: footerH, fill: ftr.band_color || "#111111" }));
    } else if (!isMinimalFooter && !isChapterFooter) {
      group.add(new Konva.Rect({ x: 0, y: footerY, width, height: footerH, fill: ftr.band_color || "#1F3A2E" }));
    }
    const footerColor = isChapterFooter
      ? "rgba(255,255,255,0.75)"
      : isMinimalFooter
        ? themeColor(resolvedTheme, "muted", "#98A2B3")
        : "rgba(255,255,255,0.5)";
    const leftX = Math.round((isMinimalFooter ? 34 : (isBadgeFooter ? 5 : (isChapterFooter ? 50 : 16))) * fontScale);
    if (ftr.logo_src) {
      const logoH = Math.round(footerH * (isBadgeFooter ? 0.42 : 0.5));
      const logoW = Math.round(logoH * LOGO_ASPECT);
      const logoY = footerY + Math.round((footerH - logoH) / 2);
      if (!isPreview) {
        group.add(new CustomImage({ url: ftr.logo_src, x: leftX, y: logoY, width: logoW, height: logoH, placeholder: false, clearImg: false, maxScale: 2, refreshCallback: imageCallback }));
      }
    } else if (ftr.left) {
      group.add(new CustomText({ x: leftX, y: textY, width: isBadgeFooter ? Math.round(62 * fontScale) : Math.round(width * 0.7), text: ftr.left, fontSize: isBadgeFooter ? Math.round(8 * fontScale) : footerFontSize, fontStyle: (isMinimalFooter || isBadgeFooter) ? "bold" : "normal", fill: isBadgeFooter ? "#FFFFFF" : footerColor, refreshCallback: imageCallback }));
    }
    if (ftr.right) group.add(new CustomText({ x: Math.round(width * 0.7), y: textY, width: Math.round(width * 0.3) - Math.round((isMinimalFooter ? 34 : 16) * fontScale), text: ftr.right, fontSize: footerFontSize, fill: footerColor, align: "right", refreshCallback: imageCallback }));
    bodyTop = Math.max(bodyTop, 0);
  }

  const footerH = (dsl.chrome && dsl.chrome.footer)
    ? Math.round((COMPACT_FOOTER_VARIANTS.includes(dsl.chrome.footer.variant) ? 24 : 36) * fontScale)
    : 0;
  const bodyHeight = height - bodyTop - footerH;
  if (dsl.flow) {
    const padding = dsl.layout?.padding || {};
    const padX = Math.round((padding.x ?? 0) * fontScale);
    const padTop = Math.round((padding.top ?? 0) * fontScale);
    const padBottom = Math.round((padding.bottom ?? 0) * fontScale);
    const contentHeight = Math.max(0, bodyHeight - padTop - padBottom);
    group.add(renderBlock({ type: "flow", direction: "vertical", children: dsl.flow }, crystalizedData, { x: padX, y: bodyTop + padTop, width: Math.max(0, width - padX * 2), height: contentHeight, imageCallback, theme: resolvedTheme, fontScale, chromeFontScale: fontScale, debug, isOutermost: true, _preview: options._preview, provenance: options.provenance, renderContextId: options.renderContextId }));
  } else if (dsl.bands) {
    const bandPadY = Math.round(20 * fontScale), bandGap = Math.round(20 * fontScale);
    const totalProp = dsl.bands.reduce((s, b) => s + (b.proportion || 1), 0);
    const available = Math.max(0, bodyHeight - bandPadY * 2 - bandGap * Math.max(0, dsl.bands.length - 1));
    let bandY = bodyTop + bandPadY;
    for (let bi = 0; bi < dsl.bands.length; bi++) {
      const band = dsl.bands[bi], bandH = ((band.proportion || 1) / totalProp) * available;
      const bg = new Konva.Group({ x: 0, y: bandY, width, height: bandH });
      if (band.background) bg.add(new Konva.Rect({ x: 0, y: 0, width, height: bandH, fill: band.background }));
      const cols = band.columns || [];
      if (cols.length > 0) {
        bg.add(renderBlock({ type: "flow", direction: "horizontal", gap: Math.round(20 * fontScale), padding_x: Math.round(20 * fontScale),
          children: cols.map(col => ({
            type: "flow", direction: "vertical",
            children: col.header ? [{ type: "section_header", text: col.header, id: col.id }, ...(col.blocks || [])] : (col.blocks || []),
            proportion: col.proportion || 1, id: col.id
          }))
        }, crystalizedData, { x: 0, y: 0, width, height: bandH, imageCallback, theme: resolvedTheme, fontScale, chromeFontScale: fontScale, debug, isOutermost: false, _preview: options._preview, provenance: options.provenance, renderContextId: options.renderContextId }));
      }
      group.add(bg); bandY += bandH;
      if (bi < dsl.bands.length - 1) bandY += bandGap;
    }
  }

  // Brand mark: a fixed-aspect logo image, absolutely positioned (unlike a
  // flow child, it can never be stretched/skewed by proportional flow
  // sizing) and drawn last so it always sits above body content — e.g. the
  // cover's top-left wordmark over its background photo.
  if (dsl.chrome?.brand_mark?.src) {
    const bm = dsl.chrome.brand_mark;
    const bmWidth = Math.round((bm.width_frac ?? 0.22) * width);
    const bmHeight = Math.round(bmWidth / LOGO_ASPECT);
    const bmX = Math.round((bm.x_frac ?? 0.1) * width);
    const bmY = Math.round((bm.y_frac ?? 0.1) * height);
    if (!isPreview) {
      group.add(new CustomImage({ url: bm.src, x: bmX, y: bmY, width: bmWidth, height: bmHeight, placeholder: false, clearImg: false, maxScale: 2, refreshCallback: imageCallback }));
    }
  }

  flattenGroupTree(group);

  // ── Clickable block overlays ──────────────────────────────────────────
  // After layout, compute each block's bounding box and add a transparent
  // Rect overlay that the InfiniteCanvas tracking system (convertItems)
  // can safely reparent without disrupting the VizDSL layout.  Simple
  // Rects survive reparenting; deeply-nested Konva shapes would not.
  // Carry source IDs and summary ID on every overlay so the viz_block
  // click handler can resolve them even after convertItems reparents
  // the overlays away from the outer viz_dsl group.
  // ── Clickable block overlays ──────────────────────────────────────────
  // After layout, compute each block's bounding box and add a transparent
  // Rect overlay that the InfiniteCanvas tracking system (convertItems)
  // can safely reparent without disrupting the VizDSL layout.  Simple
  // Rects survive reparenting; deeply-nested Konva shapes would not.
  // Carry source IDs and summary ID on every overlay so the viz_block
  // click handler can resolve them even after convertItems reparents
  // the overlays away from the outer viz_dsl group.
  //
  // Always create overlays when we have a summary primitive ID — even if
  // source IDs aren't available yet (flow needs re-crystallizing after
  // the server-side source-map change).  The handler falls back to showing
  // the summary primitive when no sources can be resolved.
  const vizSourceIds = options._vizSourceIds || [];
  const vizSummaryId = options._vizSummaryId || null;
  if (vizSummaryId) {
    const toLocal = group.getAbsoluteTransform().copy().invert();
    const overlays = []; // {x,y,w,h, blockId}

    // Prefer evidence-tagged shapes for field-level overlays.
    // Use _evidenceProvKey (exact provenance key like "key_signals.Financial stability")
    // for precise source attribution per field.
    const collectEvidence = (node) => {
      const ev = node.getAttr && node.getAttr('_evidence');
      const provKey = node.getAttr && node.getAttr('_evidenceProvKey');
      const bid = node.getAttr && node.getAttr('_blockId');
      if (provKey && (node.width() > 0 || node.height() > 0)) {
        // Field-level overlay: min 8px with 2px padding so tiny shapes (dots/badges) are clickable
        const absPos = node.getAbsolutePosition();
        const localPos = toLocal.point(absPos);
        const w = Math.max(8, (node.width() || 0) * Math.abs(node.scaleX() || 1));
        const h = Math.max(8, (node.height() || 0) * Math.abs(node.scaleY() || 1));
        overlays.push({ x: localPos.x - 2, y: localPos.y - 2, w: w + 4, h: h + 4, blockId: provKey });
      }
      if (node.getChildren) for (const c of node.getChildren()) collectEvidence(c);
    };
    collectEvidence(group);

    // Fall back to per-block bounding boxes if no evidence shapes found
    if (overlays.length === 0) {
      const blockBounds = new Map();
      const collect = (node) => {
        const bid = node.getAttr && node.getAttr('_blockId');
        if (bid) {
          const absPos = node.getAbsolutePosition();
          const localPos = toLocal.point(absPos);
          const w = (node.width() || 0) * Math.abs(node.scaleX() || 1);
          const h = (node.height() || 0) * Math.abs(node.scaleY() || 1);
          const existing = blockBounds.get(bid);
          if (existing) {
            existing.x1 = Math.min(existing.x1, localPos.x);
            existing.y1 = Math.min(existing.y1, localPos.y);
            existing.x2 = Math.max(existing.x2, localPos.x + w);
            existing.y2 = Math.max(existing.y2, localPos.y + h);
          } else {
            blockBounds.set(bid, { x1: localPos.x, y1: localPos.y, x2: localPos.x + w, y2: localPos.y + h });
          }
        }
        if (node.getChildren) for (const c of node.getChildren()) collect(c);
      };
      collect(group);
      for (const [blockId, b] of blockBounds) {
        overlays.push({ x: b.x1, y: b.y1, w: Math.max(1, b.x2 - b.x1), h: Math.max(1, b.y2 - b.y1), blockId });
      }
    }

    for (const o of overlays) {
      const overlay = new Konva.Rect({
        x: o.x, y: o.y, width: o.w, height: o.h,
        fill: 'transparent',
        name: 'inf_track viz_block',
        _blockId: o.blockId
      });
      overlay.setAttr('renderContextId', options.renderContextId);
      if (vizSourceIds.length > 0) overlay.setAttr('_vizSourceIds', vizSourceIds);
      overlay.setAttr('_vizSummaryId', vizSummaryId);
      group.add(overlay);
    }
  }

  return group;
}

export default { renderVizDslSlide, DUMMY_DSL, DUMMY_CRYSTALIZED };
